import { Renew } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import { Body, Button, Tooltip } from 'vet-bones/design-system/components';

import {
  SDU_DASHBOARD_LAST_SYNCED,
  SDU_DASHBOARD_SYNC_ALL,
} from 'src/ui/constants/testIds';
import { useHasReadLogsPermissionsForConnections } from 'src/ui/containers/endpoints/utils/useHasReadLogsPermissions';
import { isSduSupportedForConnection } from 'src/ui/containers/endpoints/utils/useIsSduSupported';
import {
  useConnectionsSyncStatus,
  useSyncAllConnections,
} from 'src/ui/containers/sdu-dashboard/utils/useSduSyncAll';
import { Connection } from 'src/ui/graph/types';
import { useProfile } from 'src/ui/hooks/connection';
import { useEndpointVersions } from 'src/ui/hooks/endpoint';
import * as copy from 'src/ui/templates/copy';
import { isPortalEnabled } from 'src/ui/utils/features';

interface SduSyncStatusProps {
  connections: Connection[];
}

export const SduSyncStatus: React.VFC<SduSyncStatusProps> = ({
  connections,
}) => {
  const { data: profileData } = useProfile();
  const isStardogEmployee = profileData?.profile?.is_staff === true;
  const portalEnabled = isPortalEnabled();

  const versionsById = useEndpointVersions(connections);

  const supportedConnections = connections.filter((c) =>
    isSduSupportedForConnection({
      connection: c,
      isPortalEnabled: portalEnabled,
      isStardogEmployee,
      version: versionsById[c.id],
    })
  );

  const permissionsById = useHasReadLogsPermissionsForConnections(
    supportedConnections
  );

  const eligibleConnections = supportedConnections.filter(
    (c) => permissionsById[c.id]
  );

  const { lastSyncedLabel, completedConnectionIds } = useConnectionsSyncStatus(
    eligibleConnections
  );

  const syncableConnections = eligibleConnections.filter(
    (c) => !completedConnectionIds.has(c.id)
  );

  const { isSyncing, syncAll } = useSyncAllConnections(syncableConnections);

  let tooltipDescription = copy.components.sduDashboard.syncAll.tooltip;
  if (!eligibleConnections.length) {
    tooltipDescription =
      copy.components.sduDashboard.syncAll.tooltipNoConnections;
  } else if (!syncableConnections.length) {
    tooltipDescription = copy.components.sduDashboard.syncAll.tooltipUpToDate;
  }

  return (
    <div className="sd-sdu-dashboard-sync-status">
      <Body size="sm" testId={SDU_DASHBOARD_LAST_SYNCED}>
        {copy.components.sduDashboard.lastSynced.label(
          lastSyncedLabel ?? copy.components.sduDashboard.lastSynced.never
        )}
      </Body>
      <Tooltip description={tooltipDescription} disabled={isSyncing}>
        <Button
          className="sd-sdu-dashboard-sync-all"
          disabled={isSyncing || !syncableConnections.length}
          leftIcon={Renew}
          leftIconProps={{
            className: classNames({
              'sd-sdu-dashboard-spin': isSyncing,
            }),
          }}
          onClick={syncAll}
          size="xs"
          testId={SDU_DASHBOARD_SYNC_ALL}
          variant="ghost-gray"
        />
      </Tooltip>
    </div>
  );
};
